import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Typography,
  Tag,
  Space,
  Button,
  Descriptions,
  Divider,
  Input,
  Modal,
  message,
  Timeline,
  Card,
  Tooltip,
  List,
  Upload,
} from 'antd';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SendOutlined,
  InboxOutlined,
  FileDoneOutlined,
  SafetyCertificateOutlined,
  PaperClipOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { ChecklistItem, ProjectTeamAssignment, Attachment } from '../../types';
import { useLookups } from '../../context/LookupsContext';
import TeamTooltip from './TeamTooltip';
import StatusChangeModal from './StatusChangeModal';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface ItemDetailDrawerProps {
  item: ChecklistItem | null;
  visible: boolean;
  onClose: () => void;
  onUpdate: () => void;
  teamAssignments?: ProjectTeamAssignment[];
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const ItemDetailDrawer: React.FC<ItemDetailDrawerProps> = ({
  item,
  visible,
  onClose,
  onUpdate,
  teamAssignments = [],
}) => {
  const { getStatusName, getGateName } = useLookups();
  const [loading, setLoading] = useState(false);
  const [naModalVisible, setNaModalVisible] = useState(false);
  const [naReason, setNaReason] = useState('');
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);

  // Load attachments when item changes
  useEffect(() => {
    if (item?.id && visible) {
      loadAttachments();
    }
  }, [item?.id, visible]);

  const loadAttachments = async () => {
    if (!item) return;
    setAttachmentsLoading(true);
    try {
      const data = await api.getAttachments(item.id);
      setAttachments(data);
    } catch (error) {
      console.error('Failed to load attachments:', error);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleUploadSubmit = async () => {
    if (!item || fileList.length === 0) return;
    setUploading(true);
    try {
      for (const file of fileList) {
        if (file.originFileObj) {
          await api.uploadAttachment(item.id, file.originFileObj);
        }
      }
      message.success(`${fileList.length} file(s) uploaded successfully`);
      setUploadModalVisible(false);
      setFileList([]);
      loadAttachments();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const beforeUpload = (file: File) => {
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('File must be smaller than 10MB');
      return false;
    }
    return false; // Prevent auto-upload
  };

  if (!item) return null;

  const ti = item.templateItem;

  // Updated workflow without in_progress
  const statusWorkflow = ['not_started', 'requested', 'received', 'resolved', 'approved'];
  const currentStatusIndex = statusWorkflow.indexOf(item.status);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'not_started':
        return <ExclamationCircleOutlined />;
      case 'requested':
        return <SendOutlined />;
      case 'received':
        return <InboxOutlined />;
      case 'resolved':
        return <FileDoneOutlined />;
      case 'approved':
        return <SafetyCertificateOutlined />;
      default:
        return null;
    }
  };

  const getStatusTagColor = (status: string): string => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'resolved':
        return 'processing';
      case 'received':
        return 'cyan';
      case 'requested':
        return 'orange';
      case 'not_started':
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'high':
        return 'red';
      case 'med':
        return 'orange';
      case 'low':
        return 'green';
      default:
        return 'default';
    }
  };

  const handleStatusButtonClick = (targetStatus: string) => {
    setPendingStatus(targetStatus);
    setStatusModalVisible(true);
  };

  const handleStatusModalSuccess = () => {
    setStatusModalVisible(false);
    setPendingStatus(null);
    loadAttachments(); // Reload attachments in case files were uploaded
    onUpdate();
  };

  const handleMarkNA = async () => {
    if (!naReason.trim()) {
      message.error('Please provide a reason for marking as N/A');
      return;
    }
    setLoading(true);
    try {
      await api.markItemNA(item.id, naReason);
      message.success('Item marked as N/A');
      setNaModalVisible(false);
      setNaReason('');
      onUpdate();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to mark as N/A');
    } finally {
      setLoading(false);
    }
  };

  const getNextStatus = (): string | null => {
    if (item.isNa) return null;
    const nextIndex = currentStatusIndex + 1;
    if (nextIndex < statusWorkflow.length) {
      return statusWorkflow[nextIndex];
    }
    return null;
  };

  const nextStatus = getNextStatus();

  const getActionButtonText = (status: string): string => {
    switch (status) {
      case 'requested':
        return 'Request';
      case 'received':
        return 'Mark Received';
      case 'resolved':
        return 'Resolve';
      case 'approved':
        return 'Approve';
      default:
        return 'Update Status';
    }
  };

  const buildTimeline = () => {
    const events: { date: string; label: string; color: string; user?: string }[] = [];

    if (item.createdAt) {
      events.push({
        date: item.createdAt,
        label: 'Created',
        color: 'gray',
      });
    }

    if (item.requestedAt) {
      events.push({
        date: item.requestedAt,
        label: 'Requested',
        color: 'orange',
        user: item.requestedBy?.name,
      });
    }

    if (item.receivedAt) {
      events.push({
        date: item.receivedAt,
        label: 'Received',
        color: 'cyan',
        user: item.receivedBy?.name,
      });
    }

    if (item.resolvedAt) {
      events.push({
        date: item.resolvedAt,
        label: 'Resolved',
        color: 'blue',
        user: item.resolvedBy?.name,
      });
    }

    if (item.approvedAt) {
      events.push({
        date: item.approvedAt,
        label: 'Approved',
        color: 'green',
        user: item.approvedBy?.name,
      });
    }

    return events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const timelineEvents = buildTimeline();

  return (
    <>
      <Drawer
        title={
          <Space>
            <Text code>{ti?.itemCode}</Text>
            <Tag>{getGateName(ti?.gateNumber || 0)}</Tag>
            {item.isNa && <Tag color="default">N/A</Tag>}
          </Space>
        }
        placement="right"
        width={600}
        onClose={onClose}
        open={visible}
        extra={
          !item.isNa && (
            <Button danger onClick={() => setNaModalVisible(true)}>
              Mark N/A
            </Button>
          )
        }
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Item Text */}
          <div>
            <Title level={5}>{ti?.itemText}</Title>
            {ti?.itemType && (
              <Tag color="cyan">{ti.itemType.replace('_', ' ').toUpperCase()}</Tag>
            )}
          </div>

          {/* Current Status */}
          <Card size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>Current Status</Text>
                <Tag color={getStatusTagColor(item.status)} icon={getStatusIcon(item.status)}>
                  {item.isNa ? 'N/A' : getStatusName(item.status)}
                </Tag>
              </div>

              {!item.isNa && (
                <>
                  {/* Status Workflow Display */}
                  <div style={{ marginTop: 16 }}>
                    <Space wrap>
                      {statusWorkflow.map((status, index) => (
                        <Tooltip key={status} title={getStatusName(status)}>
                          <Tag
                            color={
                              index < currentStatusIndex
                                ? 'green'
                                : index === currentStatusIndex
                                ? getStatusTagColor(status)
                                : 'default'
                            }
                            style={{
                              opacity: index > currentStatusIndex ? 0.5 : 1,
                            }}
                          >
                            {index < currentStatusIndex && <CheckCircleOutlined />}
                            {index === currentStatusIndex && getStatusIcon(status)}
                            {` ${getStatusName(status)}`}
                          </Tag>
                        </Tooltip>
                      ))}
                    </Space>
                  </div>

                  {/* Quick Status Update */}
                  {nextStatus && (
                    <Button
                      type="primary"
                      onClick={() => handleStatusButtonClick(nextStatus)}
                      loading={loading}
                      style={{ marginTop: 16 }}
                      block
                    >
                      {getActionButtonText(nextStatus)}
                    </Button>
                  )}
                </>
              )}

              {item.isNa && item.naReason && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">Reason:</Text>
                  <Paragraph style={{ margin: '4px 0 0 0' }}>{item.naReason}</Paragraph>
                </div>
              )}
            </Space>
          </Card>

          <Divider style={{ margin: '8px 0' }} />

          {/* Details */}
          <Descriptions column={1} size="small">
            {item.priority && (
              <Descriptions.Item label="Priority">
                <Tag color={getPriorityColor(item.priority)}>{item.priority.toUpperCase()}</Tag>
              </Descriptions.Item>
            )}
            {ti?.proofType && (
              <Descriptions.Item label="Proof Type">
                {ti.proofType.replace(/_/g, ' ')}
              </Descriptions.Item>
            )}
            {ti?.slaDays && (
              <Descriptions.Item label="SLA">{ti.slaDays} days</Descriptions.Item>
            )}
            {item.dueDate && (
              <Descriptions.Item label="Due Date">
                {dayjs(item.dueDate).format('MMM D, YYYY')}
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* Response (if exists) */}
          {item.response && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Title level={5} style={{ marginBottom: 8 }}>Response</Title>
                <Card size="small" style={{ backgroundColor: '#f5f5f5' }}>
                  <Paragraph style={{ margin: 0 }}>{item.response}</Paragraph>
                </Card>
              </div>
            </>
          )}

          {/* Attachments */}
          <Divider style={{ margin: '8px 0' }} />
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Title level={5} style={{ margin: 0 }}>
                <PaperClipOutlined /> Attachments
              </Title>
              <Button
                type="link"
                icon={<UploadOutlined />}
                onClick={() => setUploadModalVisible(true)}
                size="small"
              >
                Add Attachment
              </Button>
            </div>
            {attachmentsLoading ? (
              <Text type="secondary">Loading...</Text>
            ) : attachments.length === 0 ? (
              <Text type="secondary">No attachments</Text>
            ) : (
              <List
                size="small"
                dataSource={attachments}
                renderItem={(att) => (
                  <List.Item
                    actions={[
                      <Button
                        type="link"
                        size="small"
                        icon={<DownloadOutlined />}
                        href={api.getAttachmentDownloadUrl(att.id)}
                        target="_blank"
                      >
                        Download
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<PaperClipOutlined />}
                      title={att.fileName}
                      description={`${formatFileSize(att.fileSizeBytes)} • ${dayjs(att.createdAt).format('MMM D, YYYY')}`}
                    />
                  </List.Item>
                )}
              />
            )}
          </div>

          {/* RACI */}
          {(ti?.approverTeams?.length || ti?.receiverTeams?.length || ti?.consultedTeams?.length || ti?.informedTeams?.length) && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Title level={5} style={{ marginBottom: 12 }}>RACI Assignment</Title>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                  Hover over team tags to see assigned contacts
                </Text>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {ti?.approverTeams && ti.approverTeams.length > 0 && (
                    <div>
                      <Text type="secondary">Approver (A):</Text>
                      <Space style={{ marginLeft: 8 }} wrap>
                        {ti.approverTeams.map((team) => (
                          <TeamTooltip
                            key={team}
                            teamCode={team}
                            teamAssignments={teamAssignments}
                            tagColor="cyan"
                          />
                        ))}
                      </Space>
                    </div>
                  )}
                  {ti?.receiverTeams && ti.receiverTeams.length > 0 && (
                    <div>
                      <Text type="secondary">Receiver (R):</Text>
                      <Space style={{ marginLeft: 8 }} wrap>
                        {ti.receiverTeams.map((team) => (
                          <TeamTooltip
                            key={team}
                            teamCode={team}
                            teamAssignments={teamAssignments}
                            tagColor="blue"
                          />
                        ))}
                      </Space>
                    </div>
                  )}
                  {ti?.consultedTeams && ti.consultedTeams.length > 0 && (
                    <div>
                      <Text type="secondary">Consulted (C):</Text>
                      <Space style={{ marginLeft: 8 }} wrap>
                        {ti.consultedTeams.map((team) => (
                          <TeamTooltip
                            key={team}
                            teamCode={team}
                            teamAssignments={teamAssignments}
                            tagColor="cyan"
                          />
                        ))}
                      </Space>
                    </div>
                  )}
                  {ti?.informedTeams && ti.informedTeams.length > 0 && (
                    <div>
                      <Text type="secondary">Informed (I):</Text>
                      <Space style={{ marginLeft: 8 }} wrap>
                        {ti.informedTeams.map((team) => (
                          <TeamTooltip
                            key={team}
                            teamCode={team}
                            teamAssignments={teamAssignments}
                            tagColor="default"
                          />
                        ))}
                      </Space>
                    </div>
                  )}
                </Space>
              </div>
            </>
          )}

          {/* Individual Assignments (when item has been requested) */}
          {(item.assignedReceiverEmail || item.assignedApproverEmail) && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Title level={5} style={{ marginBottom: 12 }}>Individual Assignments</Title>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {item.assignedReceiverEmail && (
                    <div>
                      <Text type="secondary">Assigned Receiver:</Text>
                      <Tag color="blue" style={{ marginLeft: 8 }}>
                        {item.assignedReceiverEmail}
                      </Tag>
                    </div>
                  )}
                  {item.assignedApproverEmail && (
                    <div>
                      <Text type="secondary">Assigned Approver:</Text>
                      <Tag color="cyan" style={{ marginLeft: 8 }}>
                        {item.assignedApproverEmail}
                      </Tag>
                    </div>
                  )}
                </Space>
              </div>
            </>
          )}

          {/* Activity Timeline */}
          {timelineEvents.length > 0 && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Title level={5} style={{ marginBottom: 12 }}>Activity</Title>
                <Timeline
                  items={timelineEvents.map((event) => ({
                    color: event.color,
                    children: (
                      <div>
                        <Text strong>{event.label}</Text>
                        {event.user && (
                          <Text type="secondary"> by {event.user}</Text>
                        )}
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {dayjs(event.date).format('MMM D, YYYY h:mm A')}
                        </Text>
                      </div>
                    ),
                  }))}
                />
              </div>
            </>
          )}

          {/* Notes */}
          {item.notes && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Title level={5} style={{ marginBottom: 8 }}>Notes</Title>
                <Paragraph>{item.notes}</Paragraph>
              </div>
            </>
          )}
        </Space>
      </Drawer>

      {/* Status Change Modal */}
      <StatusChangeModal
        visible={statusModalVisible}
        item={item}
        targetStatus={pendingStatus || ''}
        onSuccess={handleStatusModalSuccess}
        onCancel={() => {
          setStatusModalVisible(false);
          setPendingStatus(null);
        }}
      />

      {/* N/A Modal */}
      <Modal
        title="Mark Item as N/A"
        open={naModalVisible}
        onOk={handleMarkNA}
        onCancel={() => {
          setNaModalVisible(false);
          setNaReason('');
        }}
        confirmLoading={loading}
        okText="Mark as N/A"
        okButtonProps={{ danger: true }}
      >
        <Paragraph type="secondary">
          This will mark the item as "Not Applicable" and exclude it from progress calculations.
        </Paragraph>
        <TextArea
          placeholder="Please provide a reason why this item is not applicable..."
          value={naReason}
          onChange={(e) => setNaReason(e.target.value)}
          rows={3}
        />
      </Modal>

      {/* Upload Attachment Modal */}
      <Modal
        title="Add Attachment"
        open={uploadModalVisible}
        onOk={handleUploadSubmit}
        onCancel={() => {
          setUploadModalVisible(false);
          setFileList([]);
        }}
        confirmLoading={uploading}
        okText="Upload"
        okButtonProps={{ disabled: fileList.length === 0 }}
      >
        <Upload.Dragger
          fileList={fileList}
          onChange={({ fileList }) => setFileList(fileList)}
          onRemove={(file) => setFileList((prev) => prev.filter((f) => f.uid !== file.uid))}
          beforeUpload={beforeUpload}
          multiple
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag file to upload
          </p>
          <p className="ant-upload-hint">
            Maximum file size: 10MB
          </p>
        </Upload.Dragger>
      </Modal>
    </>
  );
};

export default ItemDetailDrawer;
